// Task entities with relationships to projects and users
export interface MockTask {
  id: string;
  title: string;
  description: string;
  projectId: string; // References MockProject
  assigneeId?: string; // References MockUser
  reporterId: string; // References MockUser
  status: 'todo' | 'in-progress' | 'review' | 'done' | 'blocked';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  tags: string[];
  dueDate?: Date;
  estimatedHours?: number;
  actualHours?: number;
  createdAt: Date;
  updatedAt: Date;
  completedAt?: Date;
}

export const mockTasks: MockTask[] = [
  // Website Redesign (p1) tasks
  {
    id: 't1',
    title: 'Create homepage mockup',
    description: 'Design new homepage layout with modern UI components',
    projectId: 'p1',
    assigneeId: 'u3', // Alice Johnson
    reporterId: 'u2', // Jane Smith
    status: 'done',
    priority: 'high',
    tags: ['Design', 'UI'],
    dueDate: new Date('2024-01-20'),
    estimatedHours: 16,
    actualHours: 14,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-18'),
    completedAt: new Date('2024-01-18'),
  },
  {
    id: 't2',
    title: 'Implement navigation component',
    description: 'Build responsive navigation with mobile menu',
    projectId: 'p1',
    assigneeId: 'u1', // John Doe
    reporterId: 'u2',
    status: 'done',
    priority: 'high',
    tags: ['Frontend', 'React'],
    dueDate: new Date('2024-01-25'),
    estimatedHours: 12,
    actualHours: 15,
    createdAt: new Date('2024-01-05'),
    updatedAt: new Date('2024-01-24'),
    completedAt: new Date('2024-01-24'),
  },
  {
    id: 't3',
    title: 'Setup SEO optimization',
    description: 'Implement meta tags and structured data',
    projectId: 'p1',
    assigneeId: 'u5', // Charlie Brown
    reporterId: 'u2',
    status: 'in-progress',
    priority: 'medium',
    tags: ['SEO', 'Frontend'],
    dueDate: new Date('2024-02-10'),
    estimatedHours: 8,
    createdAt: new Date('2024-01-10'),
    updatedAt: new Date('2024-01-15'),
  },
  {
    id: 't4',
    title: 'Design footer component',
    description: 'Create footer design with all necessary links',
    projectId: 'p1',
    assigneeId: 'u8', // Fiona Green
    reporterId: 'u2',
    status: 'review',
    priority: 'low',
    tags: ['Design', 'UI'],
    dueDate: new Date('2024-02-05'),
    estimatedHours: 6,
    actualHours: 7,
    createdAt: new Date('2024-01-12'),
    updatedAt: new Date('2024-01-30'),
  },

  // Mobile App Development (p2) tasks
  {
    id: 't5',
    title: 'Setup React Native project',
    description: 'Initialize React Native project with navigation',
    projectId: 'p2',
    assigneeId: 'u16', // Nina Anderson
    reporterId: 'u6', // Diana Prince
    status: 'done',
    priority: 'urgent',
    tags: ['Mobile', 'React Native', 'Setup'],
    dueDate: new Date('2023-11-10'),
    estimatedHours: 8,
    actualHours: 10,
    createdAt: new Date('2023-11-01'),
    updatedAt: new Date('2023-11-09'),
    completedAt: new Date('2023-11-09'),
  },
  {
    id: 't6',
    title: 'Implement authentication flow',
    description: 'Build login, signup, and password reset screens',
    projectId: 'p2',
    assigneeId: 'u4', // Bob Williams
    reporterId: 'u6',
    status: 'done',
    priority: 'urgent',
    tags: ['Mobile', 'Auth', 'Backend'],
    dueDate: new Date('2023-12-01'),
    estimatedHours: 20,
    actualHours: 24,
    createdAt: new Date('2023-11-10'),
    updatedAt: new Date('2023-11-30'),
    completedAt: new Date('2023-11-30'),
  },
  {
    id: 't7',
    title: 'Build dashboard screen',
    description: 'Create main dashboard with key metrics',
    projectId: 'p2',
    assigneeId: 'u1', // John Doe
    reporterId: 'u6',
    status: 'in-progress',
    priority: 'high',
    tags: ['Mobile', 'UI', 'Dashboard'],
    dueDate: new Date('2024-01-30'),
    estimatedHours: 16,
    actualHours: 12,
    createdAt: new Date('2023-12-15'),
    updatedAt: new Date('2024-01-15'),
  },
  {
    id: 't8',
    title: 'Implement push notifications',
    description: 'Setup push notification service for iOS and Android',
    projectId: 'p2',
    assigneeId: 'u5', // Charlie Brown
    reporterId: 'u6',
    status: 'review',
    priority: 'high',
    tags: ['Mobile', 'Notifications'],
    dueDate: new Date('2024-02-15'),
    estimatedHours: 12,
    actualHours: 14,
    createdAt: new Date('2024-01-05'),
    updatedAt: new Date('2024-01-14'),
  },

  // CRM System Integration (p3) tasks
  {
    id: 't9',
    title: 'API integration planning',
    description: 'Document CRM API endpoints and data models',
    projectId: 'p3',
    assigneeId: 'u4', // Bob Williams
    reporterId: 'u2',
    status: 'done',
    priority: 'high',
    tags: ['Backend', 'API', 'Planning'],
    dueDate: new Date('2024-01-25'),
    estimatedHours: 12,
    actualHours: 10,
    createdAt: new Date('2024-01-05'),
    updatedAt: new Date('2024-01-24'),
    completedAt: new Date('2024-01-24'),
  },
  {
    id: 't10',
    title: 'Setup database migrations',
    description: 'Create migration scripts for CRM data',
    projectId: 'p3',
    assigneeId: 'u26', // Xavier Woods
    reporterId: 'u2',
    status: 'in-progress',
    priority: 'high',
    tags: ['Backend', 'Database'],
    dueDate: new Date('2024-02-15'),
    estimatedHours: 16,
    createdAt: new Date('2024-01-15'),
    updatedAt: new Date('2024-01-15'),
  },

  // Marketing Campaign Q1 (p4) tasks
  {
    id: 't11',
    title: 'Create social media content',
    description: 'Design and write social media posts for campaign',
    projectId: 'p4',
    assigneeId: 'u20', // Rachel Green
    reporterId: 'u12', // Julia Roberts
    status: 'in-progress',
    priority: 'high',
    tags: ['Marketing', 'Content'],
    dueDate: new Date('2024-02-01'),
    estimatedHours: 20,
    actualHours: 15,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-14'),
  },
  {
    id: 't12',
    title: 'Setup email campaigns',
    description: 'Configure automated email sequences',
    projectId: 'p4',
    assigneeId: 'u12',
    reporterId: 'u12',
    status: 'review',
    priority: 'medium',
    tags: ['Marketing', 'Email'],
    dueDate: new Date('2024-02-10'),
    estimatedHours: 10,
    actualHours: 12,
    createdAt: new Date('2024-01-08'),
    updatedAt: new Date('2024-01-15'),
  },

  // Security Audit 2024 (p5) tasks
  {
    id: 't13',
    title: 'Penetration testing',
    description: 'Conduct comprehensive penetration testing',
    projectId: 'p5',
    assigneeId: 'u21', // Sam Wilson
    reporterId: 'u21',
    status: 'in-progress',
    priority: 'urgent',
    tags: ['Security', 'Testing'],
    dueDate: new Date('2024-01-25'),
    estimatedHours: 40,
    actualHours: 30,
    createdAt: new Date('2024-01-10'),
    updatedAt: new Date('2024-01-15'),
  },
  {
    id: 't14',
    title: 'Review security policies',
    description: 'Update and document security policies',
    projectId: 'p5',
    assigneeId: 'u7', // Ethan Hunt
    reporterId: 'u21',
    status: 'todo',
    priority: 'high',
    tags: ['Security', 'Documentation'],
    dueDate: new Date('2024-02-05'),
    estimatedHours: 16,
    createdAt: new Date('2024-01-12'),
    updatedAt: new Date('2024-01-12'),
  },

  // Data Platform Migration (p6) tasks
  {
    id: 't15',
    title: 'Cloud infrastructure setup',
    description: 'Setup AWS infrastructure for data platform',
    projectId: 'p6',
    assigneeId: 'u11', // Ian Foster
    reporterId: 'u19', // Quincy Jones
    status: 'blocked',
    priority: 'high',
    tags: ['DevOps', 'Cloud', 'AWS'],
    dueDate: new Date('2024-01-30'),
    estimatedHours: 24,
    createdAt: new Date('2023-12-15'),
    updatedAt: new Date('2024-01-08'),
  },
  {
    id: 't16',
    title: 'Data pipeline design',
    description: 'Design ETL pipelines for data migration',
    projectId: 'p6',
    assigneeId: 'u19',
    reporterId: 'u19',
    status: 'todo',
    priority: 'medium',
    tags: ['Data', 'ETL'],
    dueDate: new Date('2024-02-15'),
    estimatedHours: 20,
    createdAt: new Date('2024-01-05'),
    updatedAt: new Date('2024-01-08'),
  },

  // Customer Portal Enhancement (p7) tasks
  {
    id: 't17',
    title: 'User feedback analysis',
    description: 'Analyze user feedback and feature requests',
    projectId: 'p7',
    assigneeId: 'u22', // Tina Turner
    reporterId: 'u27', // Yara Martinez
    status: 'done',
    priority: 'medium',
    tags: ['Research', 'UX'],
    dueDate: new Date('2024-01-15'),
    estimatedHours: 8,
    actualHours: 10,
    createdAt: new Date('2024-01-08'),
    updatedAt: new Date('2024-01-14'),
    completedAt: new Date('2024-01-14'),
  },
  {
    id: 't18',
    title: 'Build account settings page',
    description: 'Create new account settings interface',
    projectId: 'p7',
    assigneeId: 'u1', // John Doe
    reporterId: 'u27',
    status: 'in-progress',
    priority: 'high',
    tags: ['Frontend', 'UI'],
    dueDate: new Date('2024-02-20'),
    estimatedHours: 16,
    actualHours: 10,
    createdAt: new Date('2024-01-10'),
    updatedAt: new Date('2024-01-15'),
  },

  // API v2 Development (p10) tasks
  {
    id: 't19',
    title: 'API documentation',
    description: 'Write OpenAPI specification for v2 API',
    projectId: 'p10',
    assigneeId: 'u4', // Bob Williams
    reporterId: 'u26', // Xavier Woods
    status: 'in-progress',
    priority: 'high',
    tags: ['API', 'Documentation'],
    dueDate: new Date('2024-02-01'),
    estimatedHours: 12,
    actualHours: 8,
    createdAt: new Date('2023-12-01'),
    updatedAt: new Date('2024-01-15'),
  },
  {
    id: 't20',
    title: 'Implement rate limiting',
    description: 'Add rate limiting middleware to API',
    projectId: 'p10',
    assigneeId: 'u5', // Charlie Brown
    reporterId: 'u26',
    status: 'review',
    priority: 'high',
    tags: ['API', 'Backend', 'Security'],
    dueDate: new Date('2024-01-28'),
    estimatedHours: 10,
    actualHours: 12,
    createdAt: new Date('2024-01-05'),
    updatedAt: new Date('2024-01-15'),
  },
  {
    id: 't21',
    title: 'Write integration tests',
    description: 'Create comprehensive integration test suite',
    projectId: 'p10',
    assigneeId: 'u7', // Ethan Hunt
    reporterId: 'u26',
    status: 'in-progress',
    priority: 'medium',
    tags: ['Testing', 'API'],
    dueDate: new Date('2024-02-10'),
    estimatedHours: 20,
    actualHours: 15,
    createdAt: new Date('2024-01-08'),
    updatedAt: new Date('2024-01-14'),
  },

  // Design System Update (p11) tasks
  {
    id: 't22',
    title: 'Audit existing components',
    description: 'Review and document all existing design components',
    projectId: 'p11',
    assigneeId: 'u3', // Alice Johnson
    reporterId: 'u10', // Hannah Davis
    status: 'done',
    priority: 'high',
    tags: ['Design', 'Documentation'],
    dueDate: new Date('2024-01-20'),
    estimatedHours: 16,
    actualHours: 18,
    createdAt: new Date('2024-01-02'),
    updatedAt: new Date('2024-01-19'),
    completedAt: new Date('2024-01-19'),
  },
  {
    id: 't23',
    title: 'Create new icon set',
    description: 'Design consistent icon set for the design system',
    projectId: 'p11',
    assigneeId: 'u18', // Pam Beesly
    reporterId: 'u10',
    status: 'in-progress',
    priority: 'medium',
    tags: ['Design', 'Icons'],
    dueDate: new Date('2024-02-15'),
    estimatedHours: 20,
    actualHours: 12,
    createdAt: new Date('2024-01-10'),
    updatedAt: new Date('2024-01-15'),
  },
  {
    id: 't24',
    title: 'Update typography guidelines',
    description: 'Define typography scales and usage guidelines',
    projectId: 'p11',
    assigneeId: 'u8', // Fiona Green
    reporterId: 'u10',
    status: 'review',
    priority: 'medium',
    tags: ['Design', 'Typography'],
    dueDate: new Date('2024-02-10'),
    estimatedHours: 8,
    actualHours: 10,
    createdAt: new Date('2024-01-12'),
    updatedAt: new Date('2024-01-15'),
  },

  // E-commerce Platform (p15) tasks
  {
    id: 't25',
    title: 'Implement payment gateway',
    description: 'Integrate Stripe payment processing',
    projectId: 'p15',
    assigneeId: 'u4', // Bob Williams
    reporterId: 'u6', // Diana Prince
    status: 'review',
    priority: 'urgent',
    tags: ['Backend', 'Payment', 'Integration'],
    dueDate: new Date('2024-01-25'),
    estimatedHours: 24,
    actualHours: 28,
    createdAt: new Date('2023-12-20'),
    updatedAt: new Date('2024-01-15'),
  },
  {
    id: 't26',
    title: 'Build product catalog',
    description: 'Create product listing and detail pages',
    projectId: 'p15',
    assigneeId: 'u1', // John Doe
    reporterId: 'u6',
    status: 'done',
    priority: 'high',
    tags: ['Frontend', 'E-commerce'],
    dueDate: new Date('2024-01-15'),
    estimatedHours: 20,
    actualHours: 22,
    createdAt: new Date('2023-12-01'),
    updatedAt: new Date('2024-01-14'),
    completedAt: new Date('2024-01-14'),
  },
  {
    id: 't27',
    title: 'Shopping cart functionality',
    description: 'Implement add to cart and checkout flow',
    projectId: 'p15',
    assigneeId: 'u5', // Charlie Brown
    reporterId: 'u6',
    status: 'in-progress',
    priority: 'urgent',
    tags: ['Frontend', 'E-commerce'],
    dueDate: new Date('2024-02-01'),
    estimatedHours: 16,
    actualHours: 12,
    createdAt: new Date('2024-01-05'),
    updatedAt: new Date('2024-01-15'),
  },

  // DevOps Automation (p16) tasks
  {
    id: 't28',
    title: 'Setup CI/CD pipeline',
    description: 'Configure GitHub Actions for automated deployments',
    projectId: 'p16',
    assigneeId: 'u11', // Ian Foster
    reporterId: 'u11',
    status: 'done',
    priority: 'high',
    tags: ['DevOps', 'CI/CD'],
    dueDate: new Date('2023-12-15'),
    estimatedHours: 16,
    actualHours: 18,
    createdAt: new Date('2023-11-15'),
    updatedAt: new Date('2023-12-14'),
    completedAt: new Date('2023-12-14'),
  },
  {
    id: 't29',
    title: 'Implement monitoring',
    description: 'Setup application and infrastructure monitoring',
    projectId: 'p16',
    assigneeId: 'u7', // Ethan Hunt
    reporterId: 'u11',
    status: 'in-progress',
    priority: 'high',
    tags: ['DevOps', 'Monitoring'],
    dueDate: new Date('2024-02-20'),
    estimatedHours: 20,
    actualHours: 15,
    createdAt: new Date('2024-01-10'),
    updatedAt: new Date('2024-01-15'),
  },

  // Employee Onboarding System (p18) tasks
  {
    id: 't30',
    title: 'Design onboarding workflow',
    description: 'Map out complete onboarding process and steps',
    projectId: 'p18',
    assigneeId: 'u14', // Laura Palmer
    reporterId: 'u14',
    status: 'done',
    priority: 'high',
    tags: ['HR', 'Planning'],
    dueDate: new Date('2024-01-15'),
    estimatedHours: 12,
    actualHours: 14,
    createdAt: new Date('2024-01-05'),
    updatedAt: new Date('2024-01-14'),
    completedAt: new Date('2024-01-14'),
  },
  {
    id: 't31',
    title: 'Build onboarding portal',
    description: 'Create web portal for new employee onboarding',
    projectId: 'p18',
    assigneeId: 'u1', // John Doe
    reporterId: 'u14',
    status: 'in-progress',
    priority: 'medium',
    tags: ['Frontend', 'HR'],
    dueDate: new Date('2024-03-01'),
    estimatedHours: 24,
    actualHours: 18,
    createdAt: new Date('2024-01-15'),
    updatedAt: new Date('2024-01-15'),
  },
  {
    id: 't32',
    title: 'Create training materials',
    description: 'Develop training content and documentation',
    projectId: 'p18',
    assigneeId: 'u9', // George Miller
    reporterId: 'u14',
    status: 'todo',
    priority: 'medium',
    tags: ['HR', 'Documentation'],
    dueDate: new Date('2024-03-15'),
    estimatedHours: 16,
    createdAt: new Date('2024-01-12'),
    updatedAt: new Date('2024-01-12'),
  },
];
